import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Container,
  IconButton,
  Drawer,
  Collapse, // <-- Import Collapse
  useTheme,
  useMediaQuery
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { addtoCart, searchBooks } from "../services/Apis"; // Make sure this import exists
import { useFilter } from "../context/FilterContext";

const filters = [
  {
    label: "Price Range",
    options: ["Up to ₹ 500", "₹ 500 to ₹ 990", "₹ 1000 to ₹ 1499"],
  },
  {
    label: "Languages",
    options: ["German", "Japanese", "Spanish", "Telugu", "English", "Hindi"],
  },
  {
    label: "Discount",
    options: [
      "10% Off or more",
      "25% Off or more",
      "35% Off or more",
      "50% Off or more",
      "60% Off or more",
      "70% Off or more",
    ],
  },
];

const Result = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Only keep this block ONCE at the top!
  const initialCategoryId = location.state?.categoryId || "";
  const initialTitle = location.state?.title || "";
  const [books, setBooks] = useState(location.state?.books || []);
  const [filterValues, setFilterValues] = useState({
    priceMin: "",
    priceMax: "",
    language: "",
    discountOffer: "",
  });
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState({});

  const handleDrawerOpen = () => setDrawerOpen(true);
  const handleDrawerClose = () => setDrawerOpen(false);


  const handleToggleFilter = (label) => {
    setOpenFilter((prev) => {
      if (prev[label]) {
        return {};
      } else {
        return { [label]: true };
      }
    });
  };

  const handleFilterChange = async (label, value) => {
    let newFilters = { ...filterValues };

    if (label === "Price Range") {
      // Toggle logic: if already selected, clear; else set
      if (
        (value === "Up to ₹ 500" && newFilters.priceMin === 0 && newFilters.priceMax === 500) ||
        (value === "₹ 500 to ₹ 990" && newFilters.priceMin === 500 && newFilters.priceMax === 990) ||
        (value === "₹ 1000 to ₹ 1499" && newFilters.priceMin === 1000 && newFilters.priceMax === 1499)
      ) {
        newFilters.priceMin = "";
        newFilters.priceMax = "";
      } else if (value === "Up to ₹ 500") {
        newFilters.priceMin = 0;
        newFilters.priceMax = 500;
      } else if (value === "₹ 500 to ₹ 990") {
        newFilters.priceMin = 500;
        newFilters.priceMax = 990;
      } else if (value === "₹ 1000 to ₹ 1499") {
        newFilters.priceMin = 1000;
        newFilters.priceMax = 1499;
      }
    } else if (label === "Languages") {
      // Toggle logic: if already selected, clear; else set
      if (newFilters.language === value) {
        newFilters.language = "";
      } else {
        newFilters.language = value;
      }
    } else if (label === "Discount") {
      const match = value.match(/^(\d+)%/);
      const discountValue = match ? Number(match[1]) : "";
      if (newFilters.discountOffer === discountValue) {
        newFilters.discountOffer = "";
      } else {
        newFilters.discountOffer = discountValue;
      }
    }
    setFilterValues(newFilters);

    // Build payload for API
    const payload = {
      ...(initialCategoryId && { categoryId: initialCategoryId }),
      ...(initialTitle && { title: initialTitle }),
      ...(newFilters.priceMin !== "" && { priceMin: newFilters.priceMin }),
      ...(newFilters.priceMax !== "" && { priceMax: newFilters.priceMax }),
      ...(newFilters.language && { language: newFilters.language }),
      ...(newFilters.discountOffer !== "" && { discountOffer: newFilters.discountOffer }),
    };

    // Fetch filtered books
    const filteredBooks = await searchBooks(payload);
    setBooks(filteredBooks);
  };

  // Fetch books when location.state changes (e.g., after a new search)
  useEffect(() => {
    const fetchBooks = async () => {
      // Reset filters when location.state changes (new search)
      setFilterValues({
        priceMin: "",
        priceMax: "",
        language: "",
        discountOffer: "",
      });
      
      // If books are passed directly, use them
      if (location.state?.books) {
        setBooks(location.state.books);
      } else {
        // Otherwise, fetch using category/title if available
        const payload = {};
        if (location.state?.categoryId) payload.categoryId = location.state.categoryId;
        if (location.state?.title) payload.title = location.state.title;
        const results = await searchBooks(payload);
        setBooks(results);
      }
    };
    fetchBooks();
  }, [location.state]);

 const { token, isLoggedIn } = useFilter()
     const handleAddToCart = async (bookId) => {
 
         if (isLoggedIn && token) {
             try {
                 await addtoCart({ bookId, quantity: 1 });
                 navigate('/cart');
             } catch (error) {
                 alert("Failed to add to cart. Please login or try again.");
             }
         } else {
             navigate("/login")
         }
     };

  const handleBuyNow = async (book) => {
         if (isLoggedIn && token) {
        try {
            navigate('/checkout', { state: book });

        } catch (error) {
            console.log("Failed to add to cart. Please login or try again.");
        }} else {
            navigate("/login")
        }
    };

  return (
    <Container
      maxWidth="xl"
      sx={{
        minHeight: "95vh",
        p: { xs: 0.5, sm: 1, md: 3 },
      }}
    >
      <Box
        sx={{
          display: { xs: "block", md: "flex" },
          gap: { xs: 0, md: 3 },
          maxWidth: 1600,
          mx: "auto",
        }}
      >
        {/* Sidebar: Hide on small screens */}
        {!isSmallScreen && (
          <Box
            sx={{
              minWidth: { xs: "100%", md: 250 },
              maxWidth: { xs: "100%", md: 260 },
              p: { xs: 0, md: 0 },
              bgcolor: "transparent",
              boxShadow: "none",
              mb: { xs: 2, md: 0 },
            }}
          >
            <Box sx={{ p: 0 }}>
              {filters.map((filter) => (
                <Box key={filter.label} sx={{ mb: 1 }}>
                  <Typography fontWeight={600} fontSize={16} mb={1}>
                    {filter.label}
                  </Typography>
                  {filter.options.map((option) => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          size="medium"
                          checked={
                            (filter.label === "Price Range" && (
                              (option === "Up to ₹ 500" && filterValues.priceMax === 500) ||
                              (option === "₹ 500 to ₹ 990" && filterValues.priceMin === 500 && filterValues.priceMax === 990) ||
                              (option === "₹ 1000 to ₹ 1499" && filterValues.priceMin === 1000 && filterValues.priceMax === 1499)
                            )) ||
                            (filter.label === "Languages" && filterValues.language === option) ||
                            (filter.label === "Discount" && filterValues.discountOffer === Number(option.match(/^(\d+)%/)?.[1]))
                          }
                          onChange={() => handleFilterChange(filter.label, option)}
                          sx={{
                            borderRadius: "4px",
                            p: 0.2,
                            '& .MuiSvgIcon-root': {
                              borderRadius: "4px",
                              fontSize: 20,
                              width: 22,
                              height: 22,
                              boxSizing: "border-box",
                            },
                            '&.Mui-checked .MuiSvgIcon-root': {
                              backgroundColor: "#fff",
                              color: "#222",
                            },
                            '&.Mui-checked': {
                              color: "#222",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography fontWeight={400} fontSize={12}>
                          {option}
                        </Typography>
                      }
                      sx={{ display: "flex", mb: 0 }}
                    />
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Results */}
        <Box sx={{
            flex: 1, px: { xs: 0.5, md: 1 },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography
              variant="h6"
              fontWeight={500}
              sx={{ fontSize: "16px" }}
            >
              Results
            </Typography>
            {isSmallScreen && (
              <IconButton
                color="primary"
                onClick={handleDrawerOpen}
                sx={{ ml: 1 }}
                aria-label="Open filters"
              >
                <i className="fas fa-filter" style={{ fontSize: 20 }} />
                <Typography sx={{ ml: 0.5, fontSize: 14 }}>Filter</Typography>
              </IconButton>
            )}
          </Box>
          <Typography
            color="text.secondary"
            mb={2}
            fontSize={12}
            fontWeight={400}
          >
            Check each product page for other buying options.
          </Typography>
          {/* No Books Found Message */}
          {books.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <Typography variant="h5" color="text.secondary" fontWeight={600}>
                Sorry! book not found
              </Typography>
            </Box>
          )}
          {/* Book Cards */}
          {books.filter(book => book.isActive).map((item, idx) => (
            <Box
              key={item.id}
              sx={{
                display: { xs: "block", sm: "flex" },
                alignItems: "stretch",
                bgcolor: "#fff",
                mb: 3,
                gap: 0,
                flexDirection: { xs: "column", sm: "row" },
                border: "1px solid #e0e0e0",
                boxShadow: "none",
                overflow: "hidden",
                maxHeight: { xs: "none", md: "230px" },
                height: { xs: "auto", md: "222px" },
                width: { xs: "100%", sm: "100%" },
                maxWidth: { xs: 370, sm: "100%" },
                mx: { xs: "auto", sm: 0 },
                position: 'relative', // Ensure overlay is relative to card
              }}
            >
              <Box
                component="img"
                src={item.bookImages?.[0]?.imageUrl}
                alt={item.title}
                onClick={() => navigate(`/book-overview/${item.id}`)}
                sx={{
                  width: { xs: "100%", sm: 175 },
                  height: { xs: 180, sm: "100%" },
                  objectFit: { xs: "contain", sm: "cover" },
                  borderRadius: 0,
                  bgcolor: "#f5f5f5",
                  flexShrink: 0,
                  display: "block",
                  p: 0,
                  m: 0,
                  cursor: "pointer" // <-- Add this for pointer cursor
                }}
              />
              {/* Book Info & Actions */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  py: 1,
                  pr: 3,
                  pl: 3,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: "stretch",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-evenly",
                  }}
                >
                  <Typography
                    fontWeight={500}
                    fontSize={"18px"}
                    mb={0.5}
                    noWrap
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    fontSize={14}
                    fontWeight={400}
                    color="text.secondary"
                    mb={0.5}
                    noWrap
                  >
                    by {item.author?.name || "Unknown Author"} &nbsp; | &nbsp; {item.publishingYear || ""}
                  </Typography>

                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    mb={0.5}
                  >
                    <Typography
                      fontWeight={600}
                      color="#D44F1C"
                      fontSize={"20px"}
                    >
                      ₹ {(item.price - (item.price * (item.discountOffer || 0) / 100)).toFixed(2)}
                    </Typography>
                    <Typography
                      fontSize={"14px"}
                      fontWeight={400}
                      color="text.secondary"
                      sx={{ textDecoration: "line-through" }}
                    >
                      MRP ₹{item.price}
                    </Typography>
                    <Typography
                      fontSize={"14px"}
                      fontWeight={400}
                      color="#43a047"
                    >
                      ({item.discountOffer}%)
                    </Typography>
                  </Stack>
                  <Typography
                    fontWeight={500}
                    color="#222"
                    fontSize={"14px"}
                    mb={0}
                  >
                    {item.delivery}
                  </Typography>
                </Box>
                {/* Actions */}
                <Box
                  sx={{
                    minWidth: 150,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    justifyContent: "center",
                    pl: 0,
                    mt: { xs: 2, sm: 0 },
                  }}
                >
                  <Button
                    disableRipple
                    disableElevation
                    variant="contained"
                    onClick={() => handleAddToCart(item.id)}
                    disabled={!item.availability}
                    sx={{
                      bgcolor: '#FFC107',
                      color: '#222',
                      fontWeight: 500,
                      fontSize: '14px',
                      borderRadius: '8px',
                      boxShadow: 'none',
                      py: 1,
                      '&:hover': {
                        bgcolor: '#ffb300',
                        boxShadow: 'none',
                      },
                      '&:focus': {
                        outline: 'none',
                      },
                      '&&.Mui-disabled': {
                        bgcolor: '#FFC107',
                        color: '#222',
                        opacity: 0.7,
                      },
                    }}
                    fullWidth
                  >
                    Add to Cart
                  </Button>
                  <Button
                    onClick={() => handleBuyNow(item)}
                    disableRipple
                    disableFocusRipple
                    variant="contained"
                    disabled={!item.availability}
                    sx={{
                      bgcolor: '#D44F1C',
                      color: '#fff',
                      fontWeight: 500,
                      fontSize: '14px',
                      borderRadius: '8px',
                      boxShadow: 'none',
                      py: 1,
                      '&:hover': {
                        bgcolor: '#b93d13',
                        boxShadow: 'none',
                      },
                      '&:focus': {
                        outline: 'none',
                      },
                      '&&.Mui-disabled': {
                        bgcolor: '#D44F1C',
                        color: '#fff',
                        opacity: 0.7,
                      },
                    }}
                    fullWidth
                  >
                    Buy Now
                  </Button>
                  { !item.availability && (
                    <Box sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: '100%',
                      height: '100%',
                      bgcolor: 'rgba(180,180,180,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                      zIndex: 10,
                    }}>
                      <Typography sx={{
                        fontWeight: 700,
                        fontSize: 28,
                        color: '#d44f1c',
                        letterSpacing: 2,
                        opacity: 1,
                        userSelect: 'none',
                        
                        
                        px: 2,
                        py: 1,
                        
                      }}>
                        OUT OF STOCK
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      {/* Filter Drawer for small screens */}
      <Drawer
        anchor="top"
        open={drawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            borderRadius: "0 0 16px 16px",
            p: 2,
            maxWidth: 420,
            mx: "auto",
            width: "90%",
          },
        }}
      >
        <Box>
          <Typography fontWeight={600} fontSize={18} mb={2}>
            Filters
          </Typography>
          {filters.map((filter) => (
            <Box key={filter.label} sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => handleToggleFilter(filter.label)}
              >
                <Typography fontWeight={500} fontSize={15}>
                  {filter.label}
                </Typography>
                <i
                  className={
                    openFilter[filter.label]
                      ? "fas fa-minus"
                      : "fas fa-plus"
                  }
                  style={{
                    fontSize: 18,
                    color: "#1976d2",
                    transition: "transform 0.2s",
                  }}
                />
              </Box>
              <Collapse in={!!openFilter[filter.label]}>
                <Box sx={{ mt: 1, pl: 1 }}>
                  {filter.options.map((option) => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          size="small"
                          checked={
                            (filter.label === "Price Range" && (
                              (option === "Up to ₹ 500" && filterValues.priceMax === 500) ||
                              (option === "₹ 500 to ₹ 990" && filterValues.priceMin === 500 && filterValues.priceMax === 990) ||
                              (option === "₹ 1000 to ₹ 1499" && filterValues.priceMin === 1000 && filterValues.priceMax === 1499)
                            )) ||
                            (filter.label === "Languages" && filterValues.language === option) ||
                            (filter.label === "Discount" && filterValues.discountOffer === Number(option.match(/^(\d+)%/)?.[1]))
                          }
                          onChange={() => handleFilterChange(filter.label, option)}
                          sx={{
                            borderRadius: "4px",
                            p: 0.2,
                            '& .MuiSvgIcon-root': {
                              borderRadius: "4px",
                              fontSize: 18,
                              width: 18,
                              height: 18,
                              boxSizing: "border-box",
                            },
                            '&.Mui-checked .MuiSvgIcon-root': {
                              backgroundColor: "#fff",
                              color: "#222",
                            },
                            '&.Mui-checked': {
                              color: "#222",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography fontWeight={400} fontSize={12}>
                          {option}
                        </Typography>
                      }
                      sx={{ display: "flex", mb: 0 }}
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          ))}
          <Button
            variant="contained"
            color="primary"
            onClick={handleDrawerClose}
            sx={{ mt: 2, borderRadius: 2 }}
            fullWidth
          >
            Close
          </Button>
        </Box>
      </Drawer>
    </Container>
  );
};

export default Result;